/**
 * 建物情報テーブル共通処理
 */
import MiiaFwConfig from '@/configs/config'
import { useApiGw } from '@/modules/miiaFw/composables/useApiGw'
import type { ErrorMessage as MiiaFwErrorMessage } from '@/modules/miiaFw/utils/miiaFwUtils'
import { AuthDeviceDB } from '@/accessors/indexedDB/authDeviceDB'
import type { QueryBuildingRequestParam, ResQueryBuilding } from '@/types/accessor/api/building'
import { cloneDeep } from 'lodash'

const authDeviceDB = AuthDeviceDB.instance
interface Request {
  function: string
  params: object
}

/**
 * 全建物情報を取得する
 * @param params {object} Postするデータのオブジェクト
 * @returns 建物情報APIの取得結果
 */
export const queryBuildingDaoApi = async (
  params: QueryBuildingRequestParam
): Promise<object[] | MiiaFwErrorMessage> => {
  const request = {
    function: 'query',
    params: { ...params }
  }

  const results: object[] = []
  let exclusiveStartKey: object | null = null
  do {
    // exclusiveStartKeyが設定されている場合はparamsに追加する
    if (exclusiveStartKey !== null) {
      request.params.exclusiveStartKey = exclusiveStartKey
    }

    const resultData = await callBuildingDaoApi(request)
    if (isResQueryBuilding(resultData)) {
      if (Array.isArray(resultData.result)) {
        results.push(...resultData.result) // データを展開して配列に追加
      }
    } else {
      return resultData // MiiaFwErrorMessageの場合はここで返す。
    }
    exclusiveStartKey = resultData.lastEvaluatedKey ?? null
  } while (exclusiveStartKey !== null)

  return results
}

/**
 * 型ガード関数
 * @param data ResQueryBuilding | MiiaFwErrorMessage
 * @returns {boolean} dataがResQueryBuilding型である場合はtrue、そうでない場合はfalse
 */
function isResQueryBuilding(data: ResQueryBuilding | MiiaFwErrorMessage): data is ResQueryBuilding {
  return 'result' in data && !('message' in data)
}

/**
 * 建物情報を1つ取得する
 * @param params {object} Postするデータのオブジェクト
 * @returns 建物情報APIの取得結果
 */
export const getBuildingDaoApi = async (params: object): Promise<object | MiiaFwErrorMessage> => {
  const request: Request = {
    function: 'get',
    params: params
  }
  return await callBuildingDaoApi(request)
}

/**
 * 家屋構造種別ごとの数を取得する
 * @param params {object} Postするデータのオブジェクト
 * @returns 建物情報APIの取得結果
 */
export const countBuildingDaoApi = async (params: object): Promise<object | MiiaFwErrorMessage> => {
  const request: Request = {
    function: 'count',
    params: params
  }
  return await callBuildingDaoApi(request)
}

/**
 * 建物情報APIを呼ぶ
 * @param request {Request} Postするデータのオブジェクト
 * @returns 区画データ、または失敗ダイアログ
 */
const callBuildingDaoApi = async (
  request: Request
): Promise<ResQueryBuilding | MiiaFwErrorMessage> => {
  // リクエストパラメーターにシステムID, deviceTokenを付与する
  const deviceToken = await authDeviceDB.getDeviceToken()
  const { invokeApiGwFunc } = useApiGw()
  const params = Object.assign(
    cloneDeep(request.params),
    { system: MiiaFwConfig.config.sysId },
    { deviceToken: deviceToken }
  )
  return await new Promise((resolve, reject) => {
    invokeApiGwFunc(
      // API Gatewayのエンドポイント
      MiiaFwConfig.config.apiGwEndpointCommon,
      // Lambda関数をコールするためのWebAPIのサブディレクトリ
      '/buildings',
      // Lambda関数に渡す引数
      {
        function: request.function,
        params: params
      },
      // Lambda関数実行前の前処理のコールバック
      () => {
        // 処理中画面の表示は表示しない
      },
      // 成功時のコールバック
      (data: ResQueryBuilding) => {
        resolve(data)
      },
      // 例外受信時のコールバック(引数: エラーメッセージ)
      (message: ResQueryBuilding) => {
        reject({ message: message })
      },
      () => {
        reject('API実行に失敗しました。(原因: アクセス回線障害など)')
      },
      // 処理完了後に実行するコールバック
      () => {
        // 処理中画面の表示は表示しない
      },
      // ローディングアイコンを表示しない
      false,
      true
    )
  })
}
